import { Button, Stack, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { SuperButton } from 'components/SuperButton';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import type { PropsWithChildren } from 'react';

import type { Integration } from '../Integrations.constants';
import { isAccountingIntegration } from '../Integrations.utils';

import { useSynchroniseIntegration } from './Integration.hooks';
import { useGetIntegrationRights } from './Integration.utils';
import { ResetButton } from './ResetButton';

type IntegrationHeaderProps = {
  name: Integration;
};

export const IntegrationHeader = ({ name }: PropsWithChildren<IntegrationHeaderProps>) => {
  const { t } = useTranslation();

  const {
    synchronize,
    synchronizePdfs,
    canSynchronize,
    nextPossibleSynchronizeDate,
    lastImportDate,
    automaticSync: isAutomaticSyncEnabled,
    canImportPdfs,
  } = useSynchroniseIntegration();

  const getIntegrationRights = useGetIntegrationRights();
  const { isActive, isConnected } = getIntegrationRights(name);

  return (
    <>
      {isActive && (
        <>
          <Stack gap={0}>
            {lastImportDate && (
              <Text size="sm" c="gray">
                {t(i18nKeys.FORM.INTEGRATION.LAST_IMPORT_THE, {
                  date: dayjs(lastImportDate).format('LLL').toString(),
                })}
              </Text>
            )}
            {canSynchronize && nextPossibleSynchronizeDate && !isAutomaticSyncEnabled && (
              <Text size="sm" c="gray">
                {t(i18nKeys.FORM.INTEGRATION.NEXT_IMPORT_THE, {
                  date: dayjs(nextPossibleSynchronizeDate).format('LLL').toString(),
                })}
              </Text>
            )}
          </Stack>
          {!isAutomaticSyncEnabled && (
            <>
              <Tooltip
                withArrow
                multiline
                offset={{ mainAxis: 10 }}
                w={250}
                arrowPosition="center"
                label={t(i18nKeys.FORM.INTEGRATION.MANUAL_SYNC_DESCRIPTION)}
              >
                <IconInfoCircle size={20} />
              </Tooltip>
              <Button
                disabled={!canSynchronize}
                onClick={synchronize}
                rightSection={<IconRefresh size={16} />}
                variant="default"
                c="grey"
              >
                {t(i18nKeys.SYNCHRONIZE)}
              </Button>
            </>
          )}
        </>
      )}
      {isAccountingIntegration(name) && isConnected ? <ResetButton /> : null}
      {isAccountingIntegration(name) && isConnected && (
        <>
          <SuperButton onClick={synchronize}>{t(i18nKeys.SYNCHRONIZE)}</SuperButton>
          <SuperButton
            disabledMessage={
              canImportPdfs ? null : t('SUPERADMIN.WARNING_MESSAGES.SYNCHRONIZE_PDFS')
            }
            onClick={synchronizePdfs}
          >
            {t(i18nKeys.SYNCHRONIZE_PDFS)}
          </SuperButton>
        </>
      )}
    </>
  );
};
